import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../hooks/useFavorites';
import EventCard from '../components/EventCard';
import './FavoritesPage.css';

interface Event { id: string; slug: string; title: string; short_description: string; category: string; age_min: number; age_max: number; venue_name: string; city: string; latitude?: number; longitude?: number; start_date: string; is_free: boolean; price_description?: string; rating_avg?: number; rating_count?: number; }

const FavoritesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { favorites, loading: favoritesLoading } = useFavorites();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    if (!favoritesLoading && favorites.size > 0) fetchFavoriteEvents();
    else if (!favoritesLoading) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, favorites, favoritesLoading, navigate]);

  const fetchFavoriteEvents = async () => {
    try {
      setLoading(true); setError(null);
      const favoriteIds = Array.from(favorites).filter(id => 
        id && 
        typeof id === 'string' && 
        id !== 'null' && 
        id !== 'undefined' &&
        id.trim() !== ''
      );
      
      // If no valid favorites, just show empty state
      if (favoriteIds.length === 0) {
        setEvents([]);
        setLoading(false);
        return;
      }
      
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .in('id', favoriteIds)
        .eq('status', 'approved')
        .or(`end_date.is.null,end_date.gte.${today}`)
        .order('start_date', { ascending: true });
      if (error) throw error;
      setEvents(data || []);
    } catch (err: any) { setError(err.message || 'Failed to load favorite events'); } finally { setLoading(false); }
  };

  if (loading || favoritesLoading) return <div className="favorites-container"><div className="loading"><div className="spinner"></div><p>Loading your favorites...</p></div></div>;
  if (error) return <div className="favorites-container"><div className="error"><p>😕 Oops! {error}</p><button onClick={fetchFavoriteEvents} className="retry-btn">Try Again</button></div></div>;

  return (
    <div className="favorites-container">
      <button className="back-button" onClick={() => navigate('/')}>← Back to Home</button>
      <div className="favorites-header"><h1>❤️ My Favorites</h1><p>Your saved events for quick reference</p></div>
      {events.length === 0 ? <div className="no-favorites"><div className="empty-state"><span className="empty-icon">💔</span><h2>No favorites yet</h2><p>Start exploring events and click the heart icon to save your favorites!</p><button onClick={() => navigate('/events')} className="browse-btn">Browse Events</button></div></div> : <div className="favorites-content"><p className="favorites-count">You have <strong>{events.length}</strong> favorite event{events.length !== 1 ? 's' : ''}</p><div className="favorites-list">{events.map(event => <EventCard key={event.id} event={event} />)}</div></div>}
    </div>
  );
};

export default FavoritesPage;
